import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "../components/Grid"; // Import the Grid component
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function StockAdjustment({ onAddStockAdjustmentClick }) {
  const [stockAdjustments, setStockAdjustments] = useState([]);
  const [selectedStockAdjustment, setSelectedStockAdjustment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("stockAdjustmentCode");

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        setUserRole(userResponse.data.role); // Set the user role
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };
  
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchStockAdjustments = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const response = await axios.get(`${API_BASE_URL}/stockAdjustments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        let stockAdjustments = response.data.stockAdjustments || [];
  
        // Sort stock adjustments by date (latest first)
        stockAdjustments.sort((a, b) => 
          new Date(b.stock_adjustment_date) - new Date(a.stock_adjustment_date)
        );
  
        const formattedStockAdjustments = stockAdjustments.map((adjustment) => ({
          id: adjustment._id,
          stockAdjustmentCode: adjustment.stock_adjustment_code || "N/A",
          reason: adjustment.reason || "N/A",
          location: adjustment.location_id?.location_name || "Unknown",
          stockAdjustmentDate: new Date(adjustment.stock_adjustment_date).toLocaleDateString("en-GB"),
          created_by: adjustment.created_by?.username || "Unknown",
          stockAdjustmentType: adjustment.stockAdjustmentType?.adjustment_category_name || "N/A",
          originalAdjustment: adjustment, // Store the original data for details view
        }));
  
        setStockAdjustments(formattedStockAdjustments);
      } catch (error) {
        console.error("Error fetching stock adjustments:", error);
        toast.error("Error fetching stock adjustments.");
      }
    };
  
    fetchStockAdjustments();
  }, []);  

  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredStockAdjustments : stockAdjustments;

    if (dataToExport.length === 0) {
      toast.warn("No stock adjustment data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Stock Adjustments");

    const headerRow = [
      "Stock Adjustment Code",
      "Location",
      "Adjustment Type",
      "Adjustment Date",
      "Created By",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((adjustment) => {
      worksheet.addRow([
        adjustment.stockAdjustmentCode,
        adjustment.location,
        adjustment.stockAdjustmentType,
        adjustment.stockAdjustmentDate,
        adjustment.created_by,
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "stock_adjustments.xlsx");
  };

  const handleStockAdjustmentClick = async (stockAdjustment) => {

    console.log("Stock Adjustment Clicked: ", stockAdjustment);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      const response = await axios.get(
        `${API_BASE_URL}/stockAdjustments/${stockAdjustment.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const detailedAdjustment = response.data.stockAdjustment;
      console.log("Detailed Stock Adjustment:", detailedAdjustment);
  
      const formattedProducts = detailedAdjustment.stockAdjustmentItems.map((item) => ({
        id: item.product_id._id,
        product_name: item.product_id.product_name,
        product_image: item.product_id.product_image,
        product_code: item.product_id.product_code,
        quantity: item.qty,
        unit: item.product_id.unit?.unit_name,
      }));

      console.log("Selected Stock Adustment:", formattedProducts);
  
      setSelectedStockAdjustment({
        ...detailedAdjustment,
        products: formattedProducts,
        reason: detailedAdjustment.reason || "N/A",
        location: detailedAdjustment.location_id.location_name,
        stockAdjustmentType: detailedAdjustment.stockAdjustmentType,
        selectedProducts: formattedProducts || [],
      });
      
    } catch (error) {
      console.error("Error fetching stock adjustment details:", error.message);
      toast.error("Error fetching stock adjustment details.");
    }
  };

  const handleClosePopup = () => {
    setSelectedStockAdjustment(null);
  };

  const filteredStockAdjustments = stockAdjustments.filter((order) =>
    order.stockAdjustmentCode?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.location?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.stockAdjustmentType?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.created_by?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.stockAdjustmentDate?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const columns = [
    { field: "stockAdjustmentCode", headerName: "Stock Adjustment Code", flex: 1 },
    { field: "stockAdjustmentDate", headerName: "Date", flex: 1 },
    { field: "location", headerName: "Location", flex: 1 },
    { field: "stockAdjustmentType", headerName: "Type", flex: 1 },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <button
          onClick={() => handleStockAdjustmentClick(params.row)}
          className="bg-yellow-500 text-white p-2 flex items-center justify-center rounded"
          style={{ width: "30px", height: "30px" }}
        >
          <FaEye className="text-white" />
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Stock Adjustments</h1>
          {(userRole === "Company Admin" || userRole === "Farm User" || userRole === "Outlet User") && (
            <button
              onClick={onAddStockAdjustmentClick}
              className="bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Add Stock Adjustment
            </button>
          )}
        </div>
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="stockAdjustmentCode">Stock Adjustment Code</option>
                <option value="location">Location</option>
                <option value="stockAdjustmentType">Stock Adjustment Type</option>
                <option value="created_by">Created By</option>
                <option value="stockAdjustmentDate">Stock Adjustment Date</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Transfer Orders"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          
          <Grid rows={filteredStockAdjustments} columns={columns} getRowId={(row) => row.stockAdjustmentCode}/>
        </div>
        {selectedStockAdjustment && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">
                {selectedStockAdjustment.stock_adjustment_code}
              </h2>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Location</label>
                <input
                  type="text"
                  value={selectedStockAdjustment.location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Stock Adjustment Type</label>
                <input
                  type="text"
                  value={selectedStockAdjustment.stockAdjustmentType?.adjustment_category_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Adjustment Reason</label>
                <textarea
                  value={selectedStockAdjustment.reason || ""}
                  readOnly
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created Date</label>
                <input
                  type="text"
                  value={selectedStockAdjustment.stock_adjustment_date || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created By</label>
                <input
                  type="text"
                  value={selectedStockAdjustment.created_by?.username || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                    Stock Adjustment Selected Products
                </h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedStockAdjustment?.products || []}
                    columns={[
                      { field: "product_code", headerName: "ID", width: 100, flex: 0.5 },
                      {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        flex: 1,
                        renderCell: (params) =>
                        params.row.product_image ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                            alt="Product"
                            className="h-16 w-16 object-cover rounded-md"
                          />
                          ) : (
                            "No Image"
                          ),
                      },
                      {
                        field: "product_name",
                        headerName: "Product Name",
                        width: 150,
                        flex: 1
                      },
                      {
                          field: "quantity",
                          headerName: "Quantity",
                          width: 150,
                          headerAlign: "right",
                          align: "right",
                          flex: 1,
                        },
                      { 
                          field: "unit", 
                          headerName: "Units", 
                          flex: 1, 
                          headerAlign: "center", 
                          align: "center" 
                        },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StockAdjustment;